import { hitTestLoanGrid, registerFeedbackGrid } from "../lib/feedback/subject";
import { setPresenceTarget } from "../lib/presence";
import { PastePreview } from "../components/PastePreview";
import { previewPaste, type PastePreview as Preview } from "../lib/paste";
// The spreadsheet: Glide Data Grid (MIT) with LendSure cells, optimistic per-cell autosave,
// Excel paste → one batch (Undo), fill handle, delete-to-clear, row tint by stage.
import "@glideapps/glide-data-grid/dist/index.css";
import {
  CompactSelection,
  DataEditor,
  GridCellKind,
  type DataEditorRef,
  type EditableGridCell,
  type EditListItem,
  type GridCell,
  type GridColumn,
  type GridKeyEventArgs,
  type GridMouseEventArgs,
  type GridSelection,
  type Item,
  type Theme,
} from "@glideapps/glide-data-grid";
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState, type ReactNode } from "react";
import { toast } from "sonner";
import type { EditableField, Loan, LoanSet } from "../lib/types";
import { changedOnly, type ColCtx, type ColSpec } from "./columns";
import { alertsRenderer, makeFileRenderer, selectRenderer, type Option } from "./cells";
import { stageTone } from "../lib/stages";
import { stageDisplayAbbr } from "../lib/loanSteps";
import { FORECAST_LABEL, LOCK_LABEL } from "../lib/stages";
import { cellStatus, markError, useSaveState } from "../lib/saveState";
import { useLoanActions, type BatchEdit } from "../lib/mutations";
import { daysBetween, dateLong } from "../lib/dates";
import { money, price3, pts3 } from "../lib/format";
import { num } from "../lib/decimal";
import { fieldLabel } from "../lib/fields";

export type DisplayRow =
  | { type: "group"; key: string; label: string; summary: string; collapsed: boolean; tone?: { solid: string; on: string } }
  | { type: "loan"; loan: Loan };

export interface GridHandle {
  focusLoan: (id: string, colId?: string) => void;
  /** Scroll a group header to the top of the grid. */
  scrollToGroup: (key: string) => void;
}

interface Props {
  rows: DisplayRow[];
  columns: ColSpec[];
  ctx: ColCtx;
  dark: boolean;
  sort: { colId: string; dir: 1 | -1 } | null;
  onSort: (colId: string) => void;
  onOpen: (loanId: string) => void;
  onToggleGroup: (key: string) => void;
  onSelectionChange: (loanIds: string[]) => void;
}

function mix(a: string, b: string, t: number): string {
  const p = (h: string) => [1, 3, 5].map((i) => parseInt(h.slice(i, i + 2), 16));
  const [x, y] = [p(a), p(b)];
  return `#${x.map((v, i) => Math.round(v * (1 - t) + y[i] * t).toString(16).padStart(2, "0")).join("")}`;
}

function gridTheme(dark: boolean, compact: boolean): Partial<Theme> {
  const fontFamily = '"Inter Variable", ui-sans-serif, system-ui, -apple-system, "Segoe UI", sans-serif';
  const common = {
    fontFamily,
    baseFontStyle: compact ? "12px" : "13px",
    headerFontStyle: "600 12px",
    editorFontSize: compact ? "12px" : "13px",
    cellHorizontalPadding: compact ? 8 : 10,
    cellVerticalPadding: 3,
    lineHeight: 1.4,
    headerIconSize: 16,
    roundingRadius: 4,
  };
  return dark
    ? {
        ...common,
        accentColor: "#7CB3FF",
        accentFg: "#0A1C38",
        accentLight: "rgba(124,179,255,0.16)",
        textDark: "#E7EDF6",
        textMedium: "#9FB0C6",
        textLight: "#7F90A8",
        textBubble: "#E7EDF6",
        bgIconHeader: "#9FB0C6",
        fgIconHeader: "#0E192B",
        textHeader: "#9FB0C6",
        textHeaderSelected: "#0A1C38",
        bgCell: "#111D31",
        bgCellMedium: "#0E192B",
        bgHeader: "#0E192B",
        bgHeaderHasFocus: "#1D3354",
        bgHeaderHovered: "#1A2A44",
        bgBubble: "#1A2A44",
        bgBubbleSelected: "#22324C",
        bgSearchResult: "#33260D",
        borderColor: "rgba(159,176,198,0.16)",
        horizontalBorderColor: "rgba(159,176,198,0.10)",
        drilldownBorder: "rgba(159,176,198,0.3)",
        linkColor: "#8FB4E6",
      }
    : {
        ...common,
        accentColor: "#2563EB",
        accentFg: "#FFFFFF",
        accentLight: "rgba(37,99,235,0.10)",
        textDark: "#14202E",
        textMedium: "#535D6C",
        textLight: "#6B7585",
        textBubble: "#14202E",
        bgIconHeader: "#535D6C",
        fgIconHeader: "#FFFFFF",
        textHeader: "#535D6C",
        textHeaderSelected: "#FFFFFF",
        bgCell: "#FFFFFF",
        bgCellMedium: "#F5F7FA",
        bgHeader: "#EDF0F4",
        bgHeaderHasFocus: "#DDE2E9",
        bgHeaderHovered: "#E3EBF5",
        bgBubble: "#EDF0F4",
        bgBubbleSelected: "#FFFFFF",
        bgSearchResult: "#FFF6E0",
        borderColor: "rgba(20,32,46,0.10)",
        horizontalBorderColor: "rgba(20,32,46,0.07)",
        drilldownBorder: "rgba(20,32,46,0.2)",
        linkColor: "#2D5A91",
      };
}

const SEM = {
  light: { danger: "#B42318", warning: "#8A4B00", success: "#2E6A0A", onDanger: "#FFFFFF", amberFill: "#F2B705", onAmber: "#14202E", dot: "#6B7585" },
  dark: { danger: "#FF8A80", warning: "#FFC266", success: "#8DD35F", onDanger: "#0A1C38", amberFill: "#FFC266", onAmber: "#0A1C38", dot: "#7F90A8" },
};

interface Tip {
  x: number;
  y: number;
  node: ReactNode;
}

export const PipelineGrid = forwardRef<GridHandle, Props>(function PipelineGrid(props, ref) {
  const { rows, columns, ctx, dark, onOpen, onToggleGroup, onSelectionChange, sort, onSort } = props;
  const actions = useLoanActions();
  const save = useSaveState();
  const gridRef = useRef<DataEditorRef>(null);
  const [selection, setSelection] = useState<GridSelection>({ columns: CompactSelection.empty(), rows: CompactSelection.empty() });
  const [widths, setWidths] = useState<Record<string, number>>({});
  const [paste, setPaste] = useState<Preview | null>(null);
  const [pasteBusy, setPasteBusy] = useState(false);
  const [pasteError, setPasteError] = useState<string | null>(null);
  const pasteGeneration = useRef(0);
  const cancelPaste = () => { pasteGeneration.current++; setPaste(null); setPasteBusy(false); setPasteError(null); };
  const [tip, setTip] = useState<Tip | null>(null);
  const sem = dark ? SEM.dark : SEM.light;
  const target = Number(ctx.settings.price_target) || 103;

  const theme = useMemo(() => gridTheme(dark, ctx.compact), [dark, ctx.compact]);
  const fileRenderer = useMemo(() => makeFileRenderer(onOpen), [onOpen]);
  const renderers = useMemo(() => [fileRenderer, selectRenderer, alertsRenderer], [fileRenderer]);

  const stageOptions: Option[] = useMemo(
    () => [
      { value: null, label: "— none" },
      ...ctx.stages.map((s) => {
        const t = stageTone(s, dark);
        return { value: s.key, label: s.label, hint: `${s.sort}/${ctx.stages.length}`, solid: t?.solid, on: t?.on };
      }),
    ],
    [ctx.stages, dark],
  );
  const tierOptions: Option[] = useMemo(
    () => [{ value: null, label: "— none" }, ...["PIN", "MONEYBALL", "HIGH", "TBD"].map((v) => ({ value: v, label: FORECAST_LABEL[v], hint: v === "MONEYBALL" ? "Moneyball" : undefined }))],
    [],
  );
  const lockOptions: Option[] = useMemo(
    () => [{ value: null, label: "— unknown" }, ...Object.entries(LOCK_LABEL).map(([value, label]) => ({ value, label }))],
    [],
  );

  const gridColumns: GridColumn[] = useMemo(
    () =>
      columns.map((c) => ({
        id: c.id,
        title: sort?.colId === c.id ? `${c.title} ${sort.dir === 1 ? "↑" : "↓"}` : c.title,
        width: widths[c.id] ?? c.width,
        themeOverride: c.kind === "derived" ? { bgCell: dark ? "#0E192B" : "#FAFBFC" } : undefined,
      })),
    [columns, widths, sort, dark],
  );

  const loanAt = useCallback((row: number): Loan | null => {
    const r = rows[row];
    return r && r.type === "loan" ? r.loan : null;
  }, [rows]);

  // ── cell content ──
  const getCellContent = useCallback(
    ([col, row]: Item): GridCell => {
      const r = rows[row];
      const c = columns[col];
      if (!r || !c) return { kind: GridCellKind.Text, data: "", displayData: "", allowOverlay: false, readonly: true };
      if (r.type === "group") {
        if (col === 0) {
          return {
            kind: GridCellKind.Text,
            data: r.label,
            displayData: `${r.collapsed ? "▶" : "▼"}  ${r.label}`,
            allowOverlay: false,
            readonly: true,
            themeOverride: r.tone
              ? { baseFontStyle: "700 11px", textDark: r.tone.on, bgCell: r.tone.solid }
              : { baseFontStyle: "700 11px", textDark: dark ? "#E7EDF6" : "#16396B" },
            cursor: "pointer",
          };
        }
        return {
          kind: GridCellKind.Text,
          data: r.summary,
          displayData: r.summary,
          allowOverlay: false,
          readonly: true,
          span: [1, columns.length - 1],
          themeOverride: { textDark: dark ? "#9FB0C6" : "#535D6C", baseFontStyle: "600 12px" },
          cursor: "pointer",
        };
      }
      const l = r.loan;
      const ro = !l.can_edit;
      const raw = c.raw(l, ctx);
      switch (c.kind) {
        case "file": {
          return {
            kind: GridCellKind.Custom,
            allowOverlay: !ro,
            readonly: ro,
            copyData: raw,
            data: {
              kind: "file-cell",
              text: l.file_name,
              loanId: l.id,
              readonly: ro,
              muted: dark ? "#7F90A8" : "#6B7585",
              hasRed: l.alerts.some((a) => a.severity === "red"),
            },
          };
        }
        case "stage": {
          const st = l.stage_key ? ctx.stageByKey.get(l.stage_key) : undefined;
          const tone = stageTone(st, dark);
          return {
            kind: GridCellKind.Custom,
            allowOverlay: !ro,
            readonly: ro,
            copyData: raw,
            data: {
              kind: "select-cell",
              variant: "stage",
              value: l.stage_key,
              // A renamed stage shows its short name here; the full name is in the hover tooltip.
              label: st ? (stageDisplayAbbr(st) ?? (ctx.compact ? (stageOptions.find((o) => o.value === st.key)?.label ?? st.label) : st.label)) : "",
              step: st ? `${st.sort}/${ctx.stages.length}` : undefined,
              solid: tone?.solid,
              on: tone?.on,
              border: tone && !dark ? mix(tone.solid, "#14202E", 0.3) : undefined,
              options: stageOptions,
              dark,
            },
          };
        }
        case "tier":
          return {
            kind: GridCellKind.Custom,
            allowOverlay: !ro,
            readonly: ro,
            copyData: raw,
            data: { kind: "select-cell", variant: "tier", value: l.forecast, label: l.forecast ? FORECAST_LABEL[l.forecast] : "", options: tierOptions, dark },
          };
        case "lock":
          return {
            kind: GridCellKind.Custom,
            allowOverlay: !ro,
            readonly: ro,
            copyData: raw,
            data: { kind: "select-cell", variant: "plain", value: l.lock_status, label: l.lock_status ? LOCK_LABEL[l.lock_status] : "", options: lockOptions, dark },
          };
        case "alerts": {
          const red = l.alerts.filter((a) => a.severity === "red").length;
          const amber = l.alerts.filter((a) => a.severity === "amber").length;
          const info = l.alerts.length - red - amber;
          return {
            kind: GridCellKind.Custom,
            allowOverlay: false,
            readonly: true,
            copyData: raw,
            cursor: red || amber ? "pointer" : undefined,
            data: {
              kind: "alerts-cell",
              red,
              amber,
              info,
              colors: { red: sem.danger, amber: sem.amberFill, onRed: sem.onDanger, onAmber: sem.onAmber },
            },
          };
        }
        case "owner":
        case "derived":
          return {
            kind: GridCellKind.Text,
            data: raw,
            displayData: c.display(l, ctx),
            allowOverlay: false,
            readonly: true,
            contentAlign: c.align,
            themeOverride: c.id === "revenue_amount" && l.revenue_amount ? { baseFontStyle: `600 ${ctx.compact ? 12 : 13}px` } : undefined,
          };
        default: {
          let themeOverride: Partial<Theme> | undefined;
          if (c.kind === "date" && l.stage_key !== "funded" && l.outcome === "active") {
            const v = l[c.field as keyof Loan] as string | null;
            if (v && c.field === "coe_date" && daysBetween(ctx.today, v) < 0) themeOverride = { textDark: sem.danger };
            if (v && c.field === "lock_expires_on" && l.lock_status === "locked") {
              const d = daysBetween(ctx.today, v);
              if (d < 0) themeOverride = { textDark: sem.danger };
              else if (d <= ctx.settings.lock_warn_days || (l.coe_date && v < l.coe_date)) themeOverride = { textDark: sem.warning };
            }
          }
          return {
            kind: GridCellKind.Text,
            data: raw,
            displayData: c.display(l, ctx),
            allowOverlay: true,
            readonly: ro,
            contentAlign: c.align,
            allowWrapping: false,
            themeOverride,
          };
        }
      }
    },
    // `save.version` forces a repaint when save dots change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rows, columns, ctx, dark, stageOptions, tierOptions, lockOptions, sem, save.version],
  );

  const getRowThemeOverride = useCallback(
    (row: number): Partial<Theme> | undefined => {
      const r = rows[row];
      if (!r) return undefined;
      if (r.type === "group") return { bgCell: dark ? "#0E192B" : "#EDF0F4" };
      const l = r.loan;
      const st = l.stage_key ? ctx.stageByKey.get(l.stage_key) : undefined;
      const tone = stageTone(st, dark);
      const o: Partial<Theme> = {};
      if (tone) o.bgCell = tone.tint;
      if (!l.can_edit) o.textDark = dark ? "#9FB0C6" : "#535D6C";
      if (l.outcome !== "active") o.textDark = dark ? "#7F90A8" : "#6B7585";
      return o;
    },
    [rows, ctx.stageByKey, dark],
  );

  // ── save dots, price target dots ──
  const drawCell = useCallback(
    (
      args: { ctx: CanvasRenderingContext2D; rect: { x: number; y: number; width: number; height: number }; col: number; row: number },
      drawContent: () => void,
    ) => {
      const r = rows[args.row];
      const spec = columns[args.col];
      if (r?.type === "loan" && spec?.id === "presence") {
        const people = (ctx.presence?.get(r.loan.id) ?? []).filter((p) => p.user_id !== ctx.userId);
        people.slice(0, 3).forEach((person, i) => {
          const g = args.ctx;
          const x = args.rect.x + 17 + i * 29, y = args.rect.y + args.rect.height / 2;
          g.fillStyle = "#16396B"; g.beginPath(); g.arc(x, y, 12, 0, Math.PI * 2); g.fill();
          g.fillStyle = "#FFFFFF"; g.font = "bold 10px Inter, sans-serif"; g.textAlign = "center"; g.textBaseline = "middle";
          g.fillText(person.name.split(/\s+/).map((n) => n[0]).slice(0, 2).join(""), x, y);
        });
        return;
      }
      drawContent();
      const c = columns[args.col];
      if (!r || r.type !== "loan" || !c) return;
      const g = args.ctx;
      const { x, y, width, height } = args.rect;
      if (c.kind === "price") {
        const p = num(r.loan.price);
        if (p !== null) {
          g.fillStyle = p < 100 ? sem.danger : p >= target ? sem.success : sem.warning;
          g.beginPath();
          if (p >= target || p < 100) g.arc(x + 9, y + height / 2, 3.5, 0, Math.PI * 2);
          else {
            // ◐ half dot = below target
            g.arc(x + 9, y + height / 2, 3.5, Math.PI / 2, (Math.PI * 3) / 2);
            g.fill();
            g.beginPath();
            g.strokeStyle = g.fillStyle;
            g.lineWidth = 1;
            g.arc(x + 9, y + height / 2, 3.2, 0, Math.PI * 2);
            g.stroke();
            return;
          }
          g.fill();
        }
      }
      if (!c.field) return;
      const st = cellStatus(r.loan.id, c.field);
      if (!st) return;
      if (st.status === "error") {
        g.fillStyle = sem.danger;
        g.beginPath();
        g.moveTo(x + width - 9, y + 1);
        g.lineTo(x + width - 1, y + 1);
        g.lineTo(x + width - 1, y + 9);
        g.closePath();
        g.fill();
      } else if (st.status === "saving") {
        g.fillStyle = sem.dot;
        g.beginPath();
        g.arc(x + width - 6, y + 6, 3, 0, Math.PI * 2);
        g.fill();
      } else {
        g.strokeStyle = sem.success;
        g.lineWidth = 1.6;
        g.beginPath();
        g.moveTo(x + width - 10, y + 6);
        g.lineTo(x + width - 7.5, y + 8.5);
        g.lineTo(x + width - 3, y + 3.5);
        g.stroke();
      }
    },
    [rows, columns, sem, target, ctx],
  );

  // ── edits ──
  const editToSet = useCallback(
    (col: number, l: Loan, v: EditableGridCell): { ok: true; set: LoanSet } | { ok: false; message: string } => {
      const c = columns[col];
      if (!c?.field || !c.parse) return { ok: true, set: {} };
      if (v.kind === GridCellKind.Custom) {
        const d = v.data as { kind: string; value?: string | null; text?: string };
        if (d.kind === "select-cell") return { ok: true, set: changedOnly({ [c.field]: d.value ?? null }, l) };
        if (d.kind === "file-cell") return { ok: true, set: changedOnly({ file_name: (d.text ?? "").replace(/\s+/g, " ").trim() }, l) };
        return { ok: true, set: {} };
      }
      if (v.kind !== GridCellKind.Text) return { ok: true, set: {} };
      const res = c.parse(v.data, l, ctx);
      return res.ok ? { ok: true, set: changedOnly(res.set, l) } : res;
    },
    [columns, ctx],
  );

  const reportParseErrors = (errs: { l: Loan; field?: EditableField; message: string }[]) => {
    if (!errs.length) return;
    errs.forEach((e) => e.field && markError(e.l.id, [e.field], e.message));
    toast.error(errs.length === 1 ? `${fieldLabel(errs[0].field ?? "")} not saved` : `${errs.length} cells couldn't be read`, {
      description: `${errs[0].l.file_name || "Loan"}: ${errs[0].message}`,
    });
  };

  const onCellEdited = useCallback(
    ([col, row]: Item, v: EditableGridCell) => {
      const l = loanAt(row);
      if (!l || !l.can_edit) return;
      const r = editToSet(col, l, v);
      if (!r.ok) return reportParseErrors([{ l, field: columns[col]?.field, message: r.message }]);
      if (Object.keys(r.set).length) void actions.patch(l, r.set);
    },
     
    [loanAt, editToSet, actions, columns],
  );

  const collectBatch = useCallback(
    (items: { col: number; row: number; build: (l: Loan) => { ok: true; set: LoanSet } | { ok: false; message: string } }[]) => {
      const byLoan = new Map<string, BatchEdit>();
      const errs: { l: Loan; field?: EditableField; message: string }[] = [];
      let readOnly = 0;
      for (const it of items) {
        const l = loanAt(it.row);
        const c = columns[it.col];
        if (!l || !c?.field) continue;
        if (!l.can_edit) {
          readOnly++;
          continue;
        }
        const res = it.build(l);
        if (!res.ok) {
          errs.push({ l, field: c.field, message: res.message });
          continue;
        }
        const e = byLoan.get(l.id) ?? { loan: l, set: {} };
        e.set = { ...e.set, ...res.set };
        byLoan.set(l.id, e);
      }
      reportParseErrors(errs);
      if (readOnly) toast(`${readOnly} cell${readOnly === 1 ? "" : "s"} skipped`, { description: "Those loans are read-only for you." });
      return [...byLoan.values()].filter((e) => Object.keys(e.set).length);
    },
     
    [loanAt, columns],
  );

  const onCellsEdited = useCallback(
    (edits: readonly EditListItem[]) => {
      if (edits.length === 1) {
        onCellEdited(edits[0].location, edits[0].value);
        return true;
      }
      const cleared = edits.every((e) => {
        const v = e.value as EditableGridCell;
        if (v.kind === GridCellKind.Text) return v.data === "";
        if (v.kind === GridCellKind.Custom) {
          const d = v.data as { value?: string | null; text?: string };
          return d.value === null || d.text === "";
        }
        return false;
      });
      const batch = collectBatch(edits.map((e) => ({ col: e.location[0], row: e.location[1], build: (l: Loan) => editToSet(e.location[0], l, e.value) })));
      void actions.batch(batch, cleared ? "Cleared" : "Filled");
      return true;
    },
    [onCellEdited, collectBatch, editToSet, actions],
  );

  const onPaste = useCallback(
    (targetCell: Item, values: readonly (readonly string[])[]) => {
      const [tc, tr] = targetCell;
      // Paste a single value into a larger selection → fill the selection (Excel behaviour).
      const range = selection.current?.range;
      const items: { col: number; row: number; build: (l: Loan) => { ok: true; set: LoanSet } | { ok: false; message: string } }[] = [];
      const parseRaw = (col: number, rawText: string) => (l: Loan) => {
        const c = columns[col];
        if (!c?.parse) return { ok: true as const, set: {} };
        const res = c.parse(rawText, l, ctx);
        // Keep invalid input for the server's normal validator and preview dialog.
        return res.ok ? res : c.field ? { ok: true as const, set: { [c.field]: rawText } } : res;
      };
      if (values.length === 1 && values[0].length === 1 && range && range.width * range.height > 1) {
        for (let r = range.y; r < range.y + range.height; r++)
          for (let c = range.x; c < range.x + range.width; c++) items.push({ col: c, row: r, build: parseRaw(c, values[0][0]) });
      } else {
        // Map pasted rows onto consecutive loan rows (group headers are skipped).
        let row = tr;
        let overflow = 0;
        for (const line of values) {
          while (row < rows.length && rows[row].type !== "loan") row++;
          if (row >= rows.length) {
            overflow++;
            continue;
          }
          line.forEach((cellText, j) => {
            const col = tc + j;
            if (col < columns.length) items.push({ col, row, build: parseRaw(col, cellText) });
          });
          row++;
        }
        if (overflow) toast(`${overflow} pasted row${overflow === 1 ? "" : "s"} didn't fit`, { description: "Add loans first with + New loan, then paste again." });
      }
      const byLoan = new Map<string, BatchEdit>();
      for (const item of items) {
        const loan = loanAt(item.row);
        if (!loan) continue;
        const parsed = item.build(loan);
        if (!parsed.ok) continue;
        const edit = byLoan.get(loan.id) ?? { loan, set: {} };
        edit.set = { ...edit.set, ...parsed.set };
        byLoan.set(loan.id, edit);
      }
      const batch = [...byLoan.values()].filter((e) => Object.keys(e.set).length);
      if (items.length === 1) {
        if (batch[0]) void actions.patch(batch[0].loan, batch[0].set);
      } else if (batch.length) {
        const generation = ++pasteGeneration.current;
        setPaste(null); setPasteError(null); setPasteBusy(true);
        void previewPaste(batch).then((preview) => {
          if (pasteGeneration.current === generation) { setPaste(preview); setPasteBusy(false); }
        }).catch((e) => {
          if (pasteGeneration.current === generation) { setPasteError(e instanceof Error ? e.message : "Preview failed"); setPasteBusy(false); }
        });
      }
      return false;
    },
    [selection, columns, ctx, rows, loanAt, actions],
  );

  // ── keyboard ──
  const onKeyDown = useCallback(
    (e: GridKeyEventArgs) => {
      const loc = e.location ?? selection.current?.cell;
      if (!loc) return;
      const l = loanAt(loc[1]);
      if (!l) {
        if ((e.key === "Enter" || e.key === " ") && rows[loc[1]]?.type === "group") {
          e.cancel();
          const g = rows[loc[1]];
          if (g.type === "group") onToggleGroup(g.key);
        }
        return;
      }
      if (e.key === " " && !e.metaKey && !e.ctrlKey && !e.altKey) {
        e.cancel();
        onOpen(l.id);
      } else if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
        e.cancel();
        onOpen(l.id);
      } else if ((e.key === "]" || e.key === "[") && l.can_edit) {
        e.cancel();
        const cur = l.stage_key ? (ctx.stageByKey.get(l.stage_key)?.sort ?? 0) : 0;
        const next = ctx.stages.find((s) => s.sort === cur + (e.key === "]" ? 1 : -1));
        if (next) void actions.patch(l, { stage_key: next.key });
      }
    },
    [selection, loanAt, rows, onToggleGroup, onOpen, ctx, actions],
  );

  const onCellClicked = useCallback(
    ([col, row]: Item) => {
      const r = rows[row];
      if (r?.type === "group") return onToggleGroup(r.key);
      const c = columns[col];
      if (r?.type === "loan" && c?.kind === "alerts" && r.loan.alerts.some((a) => a.severity !== "info")) onOpen(r.loan.id);
    },
    [rows, columns, onToggleGroup, onOpen],
  );

  // ── hover explainer / error tooltip ──
  const onItemHovered = useCallback(
    (args: GridMouseEventArgs) => {
      if (args.kind !== "cell") return setTip(null);
      const [col, row] = args.location;
      const r = rows[row];
      const c = columns[col];
      if (!r || r.type !== "loan" || !c) return setTip(null);
      const l = r.loan;
      const st = c.field ? cellStatus(l.id, c.field) : undefined;
      let node: ReactNode = null;
      if (st?.status === "error") {
        node = (
          <>
            <div className="font-semibold text-danger">Not saved</div>
            <div>{st.message}</div>
          </>
        );
      } else if ((c.id === "revenue_amount" || c.id === "rev_pct" || c.id === "price") && l.price) {
        const p = Number(l.price);
        const atTarget = num(l.amount) !== null ? (Number(l.amount) * (target - 100)) / 100 : null;
        node = (
          <>
            <div className="eyebrow mb-1 text-ink-3">Revenue</div>
            <div className="tnum">
              Price {price3(l.price)} → {pts3(l.rev_pct)} pts
            </div>
            {l.amount ? (
              <div className="tnum">
                {money(l.amount)} × {pts3(l.rev_pct)}% = <b>{money(l.revenue_amount)}</b>
              </div>
            ) : (
              <div className="text-ink-3">Add an amount to see revenue</div>
            )}
            {p < target && atTarget !== null ? (
              <div className="mt-1 tnum text-warning">
                ◐ {(target - p).toFixed(3)} below {target.toFixed(3)} target · at target {money(atTarget)}
              </div>
            ) : p >= target ? (
              <div className="mt-1 text-success">● At or above {target.toFixed(3)} target</div>
            ) : null}
            {l.comp?.commission ? (
              <div className="mt-1 tnum text-ink-2">
                Comp {l.comp.bps} bps = {money(l.comp.commission)}
              </div>
            ) : null}
          </>
        );
      } else if (c.kind === "alerts" && l.alerts.length) {
        node = (
          <ul className="space-y-0.5">
            {l.alerts.map((a) => (
              <li key={a.code} className={a.severity === "red" ? "text-danger" : a.severity === "amber" ? "text-warning" : "text-ink-2"}>
                {a.severity === "red" ? "⛔" : a.severity === "amber" ? "⚠" : "ⓘ"} {a.message}
              </li>
            ))}
          </ul>
        );
      } else if (c.kind === "stage" && l.stage_key && stageDisplayAbbr({ key: l.stage_key })) {
        const s = ctx.stageByKey.get(l.stage_key);
        if (s) node = <div>{`${s.sort}. ${s.label}`}</div>;
      } else if (c.id === "presence") {
        node = <div>{c.display(l, ctx) || "Nobody else is here"}</div>;
      } else if (c.kind === "date") {
        const v = l[c.field as keyof Loan] as string | null;
        if (v) node = <div className="tnum">{dateLong(v)}</div>;
      } else if (c.kind === "longtext" && l.notes && l.notes.length > 30) {
        node = <div className="max-w-xs whitespace-pre-wrap">{l.notes}</div>;
      }
      if (!node) return setTip(null);
      setTip({ x: args.bounds.x, y: args.bounds.y + args.bounds.height + 4, node });
    },
    [rows, columns, target, ctx],
  );

  const pendingFocusRef = useRef<{ col: number; row: number } | null>(null);

  const onGridSelectionChange = useCallback(
    (s: GridSelection) => {
      const pending = pendingFocusRef.current;
      if (
        pending &&
        (pending.col !== 0 || pending.row !== 0) &&
        s.current &&
        s.current.cell[0] === 0 &&
        s.current.cell[1] === 0
      ) {
        return;
      }
      setSelection(s);
      if (s.current) {
        const loan = loanAt(s.current.cell[1]);
        setPresenceTarget(loan?.id ?? null);
      }
      const set = new Set<string>();
      const addRow = (row: number) => {
        const r = rows[row];
        if (r?.type === "loan") set.add(r.loan.id);
      };
      const ranges = s.current ? [s.current.range, ...s.current.rangeStack] : [];
      for (const rg of ranges) for (let i = rg.y; i < rg.y + rg.height; i++) addRow(i);
      for (const i of s.rows) addRow(i);
      onSelectionChange(set.size > 1 ? [...set] : []);
    },
    [rows, loanAt, onSelectionChange],
  );

  useEffect(() => {
    const pending = pendingFocusRef.current;
    if (
      pending &&
      selection.current &&
      selection.current.cell[0] === pending.col &&
      selection.current.cell[1] === pending.row
    ) {
      gridRef.current?.scrollTo(pending.col, pending.row, "both", 0, 0, { vAlign: "center" });
      gridRef.current?.focus();
      pendingFocusRef.current = null;
    }
  }, [selection]);

  useImperativeHandle(
    ref,
    () => ({
      focusLoan: (id, colId) => {
        const row = rows.findIndex((r) => r.type === "loan" && r.loan.id === id);
        if (row < 0) return;
        const col = Math.max(0, columns.findIndex((c) => c.id === (colId ?? "file_name")));
        const cell: Item = [col, row];
        pendingFocusRef.current = { col, row };
        setSelection({
          columns: CompactSelection.empty(),
          rows: CompactSelection.empty(),
          current: { cell, range: { x: col, y: row, width: 1, height: 1 }, rangeStack: [] },
        });
        setPresenceTarget(id);
        gridRef.current?.scrollTo(col, row, "both", 0, 0, { vAlign: "center" });
      },
      scrollToGroup: (key) => {
        const row = rows.findIndex((r) => r.type === "group" && r.key === key);
        if (row >= 0) gridRef.current?.scrollTo(0, row, "vertical", 0, 0, { vAlign: "start" });
      },
    }),
    [rows, columns],
  );

  const rowHeight = ctx.compact ? 32 : 40;

  return (
    <div data-feedback-grid="true" data-feedback-loan-list="true" ref={(el) => {
      if (el) registerFeedbackGrid(el, (click) => {
        // Glide bounds account for scroll, collapsed groups and density; the canvas has no DOM rows.
        return hitTestLoanGrid(rows.length, (i) => rows[i]?.type === "loan" ? rows[i].loan.id : null,
          (i) => gridRef.current?.getBounds(0, i), el.getBoundingClientRect(), selection.current?.cell[1] ?? -1, click);
      }, (loan) => {
        const row = rows.findIndex((r) => r.type === "loan" && r.loan.id === loan);
        if (row < 0) return null;
        return hitTestLoanGrid(rows.length, (i) => rows[i]?.type === "loan" ? rows[i].loan.id : null,
          (i) => gridRef.current?.getBounds(0, i), el.getBoundingClientRect(), row)?.rect ?? null;
      });
    }} className="relative h-full w-full" onMouseLeave={() => setTip(null)}>
      <PastePreview preview={paste} busy={pasteBusy} error={pasteError} onCancel={cancelPaste} onApply={() => {
        if (paste) { void actions.batch(paste.edits, "Pasted"); cancelPaste(); }
      }} />
      <DataEditor
        ref={gridRef}
        width="100%"
        height="100%"
        columns={gridColumns}
        rows={rows.length}
        getCellContent={getCellContent}
        customRenderers={renderers}
        theme={theme}
        rowHeight={(row) => (rows[row]?.type === "group" ? (ctx.compact ? 28 : 32) : rowHeight)}
        headerHeight={ctx.compact ? 32 : 36}
        freezeColumns={1}
        smoothScrollX
        smoothScrollY
        rowMarkers="none"
        rangeSelect="multi-rect"
        columnSelect="none"
        rowSelect="multi"
        fillHandle
        allowedFillDirections="vertical"
        getCellsForSelection
        onPaste={onPaste}
        onCellActivated={([col, row]) => { const loan = loanAt(row); setPresenceTarget(loan?.id ?? null, loan?.can_edit ? columns[col]?.field : undefined); }}
        onCellEdited={onCellEdited}
        onCellsEdited={onCellsEdited}
        onDelete={() => true}
        onKeyDown={onKeyDown}
        onCellClicked={onCellClicked}
        onItemHovered={onItemHovered}
        onHeaderClicked={(col) => columns[col] && onSort(columns[col].id)}
        onColumnResize={(c, w) => setWidths((p) => ({ ...p, [String(c.id)]: w }))}
        gridSelection={selection}
        onGridSelectionChange={onGridSelectionChange}
        getRowThemeOverride={getRowThemeOverride}
        drawCell={drawCell}
        keybindings={{ search: false, downFill: true, rightFill: false, selectAll: true, selectRow: true, selectColumn: false }}
        scaleToRem={false}
      />
      {tip ? (
        <div
          role="tooltip"
          className="pointer-events-none fixed z-50 max-w-sm rounded-md border border-line bg-raised px-3 py-2 text-[12px] leading-[18px] text-ink shadow-[var(--shadow-2)]"
          style={{ left: Math.min(tip.x, window.innerWidth - 320), top: tip.y }}
        >
          {tip.node}
        </div>
      ) : null}
    </div>
  );
});
